import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '../../components/site/SiteLayout';
import { PillLink } from '../../components/site/Pill';
import { SchemeGrid } from '../../components/site/SchemeGrid';
import { Reveal } from '../../components/site/Reveal';
import { INTERIORS } from '../../lib/site';

/**
 * The homepage, section for section as lotusliving.co.uk — with one change the brief
 * asked for: where the site sends people to an enquiry form, we show what is actually
 * available, live from the platform.
 */
export default function SiteHome() {
  return (
    <SiteLayout overlay>
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-ink-900">
        <img src={INTERIORS.hero} alt="" className="rise-zoom absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/60 via-ink-900/20 to-ink-900/65" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
          <p className="rise eyebrow !text-bronze-300" style={{ '--i': 0 } as React.CSSProperties}>Greater London · Dubai</p>
          <h1 className="rise h-hero mt-5 max-w-[16ch] text-white sm:mt-6" style={{ '--i': 1 } as React.CSSProperties}>
            Homes Designed for <em>Modern Renters</em>
          </h1>
          <div className="rise mt-9 sm:mt-11" style={{ '--i': 2 } as React.CSSProperties}>
            <PillLink to="/rent" variant="white">Find your new home</PillLink>
          </div>
        </div>
        <a href="#belong" className="rise-fade absolute bottom-6 left-1/2 z-10 hidden h-11 w-11 -translate-x-1/2 place-items-center text-white/75 transition hover:text-white sm:bottom-10 sm:grid" style={{ '--i': 6 } as React.CSSProperties} aria-label="Scroll">
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </a>
      </section>

      {/* ── Two-column text ──────────────────────────────────────────────────── */}
      <section id="belong" className="site-narrow section-y grid scroll-mt-20 gap-8 md:grid-cols-2 md:gap-16 lg:gap-24">
        <Reveal as="h2" className="h-section text-ink-900">More Than a Place to Live. A Place to Belong.</Reveal>
        <Reveal delay={120} className="md:pt-2">
          <p className="lede max-w-[620px] text-ink-600">
            Renting should never mean compromising on quality, comfort or experience. Our spacious, stylish, well-located
            homes are purpose-built for renters, then designed, built and managed by us.
          </p>
          <PillLink to="/rent" className="mt-8 lg:mt-10">Find your new home</PillLink>
        </Reveal>
      </section>

      {/* ── Floating gallery ─────────────────────────────────────────────────── */}
      <section className="site-container section-b">
        <div className="grid grid-cols-6 gap-3 sm:gap-5 lg:gap-7">
          <Photo src={INTERIORS.bedroom} className="col-span-3 aspect-[3/4] sm:col-span-2 lg:mt-16" />
          <Photo src={INTERIORS.harlow} delay={100} className="col-span-3 aspect-[3/4] sm:col-span-4 sm:aspect-[16/9]" />
          <Photo src={INTERIORS.kitchen} className="col-span-6 aspect-[16/9] sm:col-span-3 sm:aspect-[4/3]" />
          <Photo src={INTERIORS.kitchenDetail} delay={100} className="col-span-3 aspect-[4/3] sm:col-span-2 sm:aspect-[3/4]" />
          <Photo src={INTERIORS.bathroom} delay={200} className="col-span-3 aspect-[4/3] sm:col-span-1 sm:aspect-[3/4] lg:mt-12" />
        </div>
      </section>

      {/* ── Simply great rental homes ────────────────────────────────────────── */}
      <section className="bg-cream">
        <div className="site-narrow section-y grid items-center gap-12 md:grid-cols-2 md:gap-14 lg:gap-24">
          <div className="relative mb-6 sm:mb-10 md:mb-0">
            <Reveal variant="image" className="overflow-hidden rounded-[10px]">
              <img src={INTERIORS.livingArea} alt="Bright open-plan kitchen, dining and living area at Drake House" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            </Reveal>
            <Reveal delay={250} className="absolute -bottom-10 -right-2 hidden w-[42%] sm:block lg:-right-10">
              <img src={INTERIORS.welcomeHamper} alt="Welcome hamper at Drake House" className="w-full rounded-[10px] border-[6px] border-cream object-cover shadow-float" loading="lazy" />
            </Reveal>
          </div>
          <Reveal>
            <p className="eyebrow">Built for renters</p>
            <h2 className="h-section mt-4 text-ink-900">Simply Great Rental Homes</h2>
            <div className="prose-site mt-6 space-y-5 text-ink-600 lg:mt-8">
              <p>Generous rooms filled with natural light, large kitchens with premium appliances, and elegant finishes like quality wood flooring and durable surfaces.</p>
              <p>Every development sits close to transport, green spaces, shops and everyday essentials.</p>
            </div>
            <Link to="/about" className="copy-link mt-7 inline-block py-2 text-[15px] font-medium">About Us</Link>
          </Reveal>
        </div>
      </section>

      {/* ── Explore properties (live availability, not an enquiry form) ──────── */}
      <section className="site-container section-y">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Live availability</p>
            <h2 className="h-section mt-4 text-ink-900">Explore Properties</h2>
          </div>
          <PillLink to="/rent" variant="ghost" small>View all</PillLink>
        </Reveal>
        <SchemeGrid className="mt-10 lg:mt-14" />
      </section>

      {/* ── Why Lotus ────────────────────────────────────────────────────────── */}
      <section className="border-t border-ink-100">
        <div className="site-narrow section-y grid gap-8 md:grid-cols-2 md:gap-16 lg:gap-24">
          <Reveal as="h2" className="h-section text-ink-900">Why Lotus?</Reveal>
          <Reveal delay={120} className="prose-site space-y-5 text-ink-600 md:pt-2">
            <p>Our homes aren't adapted for renters; they're made for them. Better layouts, smarter amenities and a genuine commitment to long-term comfort.</p>
            <p>High-spec finishes, energy-efficient design and responsive on-site management, with every detail chosen with renters in mind.</p>
            <PillLink to="/rent" className="!mt-8 lg:!mt-10">Find your new home</PillLink>
          </Reveal>
        </div>
      </section>

      {/* ── Banner ───────────────────────────────────────────────────────────── */}
      <section className="relative flex min-h-[60vh] items-center justify-center overflow-hidden bg-ink-900 sm:min-h-[70vh]">
        <Reveal variant="image" className="absolute inset-0">
          <img src={INTERIORS.kitchen} alt="" className="h-full w-full object-cover" loading="lazy" />
        </Reveal>
        <div className="absolute inset-0 bg-ink-900/55" />
        <Reveal className="relative z-10 px-6 py-24 text-center sm:py-28">
          <h2 className="h-section mx-auto max-w-[14ch] text-white sm:max-w-[18ch]">Start Living the Way You Deserve</h2>
          <PillLink to="/rent" variant="white" className="mt-8 lg:mt-12">Find your new home</PillLink>
        </Reveal>
      </section>
    </SiteLayout>
  );
}

function Photo({ src, className = '', delay = 0 }: { src: string; className?: string; delay?: number }) {
  return (
    <Reveal variant="image" delay={delay} className={`overflow-hidden rounded-[10px] bg-ink-100 ${className}`}>
      <img src={src} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.03]" />
    </Reveal>
  );
}
